import { useRef, useState, type FormEvent } from 'react';
import {
  LIBELLES_RAISON_PROPOSITION_CV,
  LIBELLES_STATUT,
  telephoneLisible,
  type CandidatProbable,
} from '@rh/shared';
import { appel } from '../api';
import { libelleSource, nomComplet } from '../format';
import { Erreur } from './Etat';

export type CandidatureProposee = CandidatProbable['candidatures'][number];

interface PropsChoix {
  candidats: readonly CandidatProbable[];
  /** « Rattacher ici », « Déplacer ici », « Déposer une copie ici ». */
  libelleAction: string;
  /** Candidature qui porte déjà le CV : pas de bouton sur elle. */
  exclure?: string | null;
  envoi: boolean;
  onChoisir: (candidat: CandidatProbable, candidature: CandidatureProposee) => void;
}

/**
 * Fiches proposées pour un CV : raisons du rapprochement, coordonnées de la
 * fiche, signaux « ne plus contacter » et « salarié », puis une ligne par
 * candidature avec son bouton — un CV se pose sur UNE candidature.
 */
export function ChoixCandidatureCv({ candidats, libelleAction, exclure = null, envoi, onChoisir }: PropsChoix) {
  return (
    <ul className="candidats-cv">
      {candidats.map((candidat) => (
        <li key={candidat.personneId} className="candidat-cv">
          <div className="ligne1">
            <strong>{nomComplet(candidat)}</strong>
            {candidat.raisons.map((raison) => (
              <span key={raison} className="raison-cv">
                {LIBELLES_RAISON_PROPOSITION_CV[raison]}
              </span>
            ))}
          </div>
          <p className="meta">
            <span>{candidat.telephone ? telephoneLisible(candidat.telephone) : 'Sans téléphone'}</span>
            <span>{candidat.email ?? 'Sans e-mail'}</span>
            {candidat.nePlusContacter && <span className="signal-alerte">Ne plus contacter</span>}
            {candidat.salarie && <span className="signal-info">Salarié en poste</span>}
          </p>
          {candidat.candidatures.length === 0 ? (
            <p className="mention">Aucune candidature disponible.</p>
          ) : (
            <ul className="candidatures-cv">
              {candidat.candidatures.map((candidature) => (
                <li key={candidature.candidatureId}>
                  <div className="corps">
                    <span className="intitule">{candidature.annonceIntitule}</span>
                    {candidature.annonceReference && <code>{candidature.annonceReference}</code>}
                    <span className="meta">
                      <span>{libelleSource(candidature.source)}</span>
                      <span>reçue le {new Date(candidature.recueLe).toLocaleDateString('fr-FR')}</span>
                      <span>{LIBELLES_STATUT[candidature.statut] ?? candidature.statut}</span>
                      {candidature.aUnCv ? <span>a déjà un CV</span> : <span className="signal-info">sans CV</span>}
                    </span>
                  </div>
                  {candidature.candidatureId === exclure ? (
                    <span className="mention">CV actuel</span>
                  ) : (
                    <button
                      type="button"
                      className="bouton"
                      disabled={envoi}
                      aria-label={`${libelleAction} : ${nomComplet(candidat)} — ${candidature.annonceIntitule}`}
                      onClick={() => onChoisir(candidat, candidature)}
                    >
                      {libelleAction}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

/** « Chercher une fiche » : nom (dans les deux ordres), 4 chiffres du téléphone ou e-mail. */
export function RechercheFicheCv(props: Omit<PropsChoix, 'candidats'>) {
  const [q, setQ] = useState('');
  const [resultats, setResultats] = useState<CandidatProbable[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [recherche, setRecherche] = useState(false);
  // Seule la dernière recherche lancée s'affiche.
  const numero = useRef(0);

  async function chercher(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    const texte = q.trim();
    if (texte.length < 3) {
      setErreur('3 caractères au moins');
      return;
    }
    numero.current += 1;
    const courante = numero.current;
    setErreur(null);
    setRecherche(true);
    try {
      const liste = await appel<CandidatProbable[]>(`/cv-a-rattacher/recherche?q=${encodeURIComponent(texte)}`);
      if (courante === numero.current) setResultats(liste);
    } catch (e) {
      if (courante === numero.current) setErreur(e instanceof Error ? e.message : 'Recherche impossible');
    } finally {
      if (courante === numero.current) setRecherche(false);
    }
  }

  return (
    <div className="recherche-fiche-cv">
      <form className="ligne-recherche" role="search" onSubmit={(e) => void chercher(e)}>
        <label className="champ">
          <span>Nom, 4 chiffres du téléphone ou e-mail</span>
          <input
            type="search"
            value={q}
            maxLength={60}
            enterKeyHint="search"
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <button type="submit" className="bouton" disabled={recherche}>
          {recherche ? 'Recherche…' : 'Chercher'}
        </button>
      </form>
      {erreur && <Erreur message={erreur} />}
      {resultats &&
        (resultats.length === 0 ? (
          <p className="mention">Aucune fiche trouvée.</p>
        ) : (
          <ChoixCandidatureCv candidats={resultats} {...props} />
        ))}
    </div>
  );
}
